import { useState, useEffect } from 'react';
import { branchesApi, Branch, BranchDocument } from '../../api/branches.api';
import { companiesApi, Company } from '../../api/companies.api';
import Table, { Column } from '../../components/common/Table';
import Button from '../../components/forms/Button';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import Input from '../../components/forms/Input';
import Select from '../../components/forms/Select';
import Checkbox from '../../components/forms/Checkbox';

const BranchList = () => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documents, setDocuments] = useState<BranchDocument[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [documentFiles, setDocumentFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    city: '',
    phone: '',
    company: '',
    isHeadquarters: false,
  });

  useEffect(() => {
    fetchBranches();
    fetchCompanies();
  }, []);

  const fetchBranches = async () => {
    try {
      setIsLoading(true);
      const data = await branchesApi.getAll();
      setBranches(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Şubeler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const data = await companiesApi.getAll();
      setCompanies(data.filter(c => c.isActive));
    } catch (err: any) {
      console.error('Şirketler yüklenirken hata:', err);
    }
  };

  const handleOpenModal = (branch?: Branch) => {
    if (branch) {
      setEditingBranch(branch);
      const companyId = typeof branch.company === 'object' && branch.company !== null
        ? (branch.company as any)._id || (branch.company as any).id
        : branch.company;
      setFormData({
        name: branch.name,
        code: branch.code,
        address: branch.address || '',
        city: branch.city || '',
        phone: branch.phone || '',
        company: companyId || '',
        isHeadquarters: branch.isHeadquarters || false,
      });
    } else {
      setEditingBranch(null);
      setFormData({
        name: '',
        code: '',
        address: '',
        city: '',
        phone: '',
        company: '',
        isHeadquarters: false,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingBranch(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingBranch) {
        await branchesApi.update(editingBranch.id, formData);
      } else {
        await branchesApi.create(formData);
      }
      await fetchBranches();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'İşlem başarısız oldu');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu şubeyi silmek istediğinizden emin misiniz?')) {
      return;
    }
    try {
      await branchesApi.delete(id);
      await fetchBranches();
    } catch (err: any) {
      setError(err.message || 'Silme işlemi başarısız oldu');
    }
  };

  const handleSync360 = async () => {
    if (!window.confirm('360Restoran\'dan şubeleri senkronize etmek istiyor musunuz?')) {
      return;
    }
    try {
      setIsSyncing(true);
      setSyncMessage(null);
      setError(null);
      const result = await branchesApi.syncFrom360();
      setSyncMessage(
        `Senkronizasyon tamamlandı: ${result.synced} şube kontrol edildi, ${result.created} yeni şube eklendi, ${result.updated} şube güncellendi.`
      );
      await fetchBranches();
      // Clear message after 5 seconds
      setTimeout(() => setSyncMessage(null), 5000);
    } catch (err: any) {
      setError(err.message || 'Senkronizasyon başarısız oldu');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await branchesApi.downloadTemplate();
    } catch (err: any) {
      setError(err.message || 'Şablon indirme başarısız oldu');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleImportExcel = async () => {
    if (!selectedFile) {
      setError('Lütfen bir Excel dosyası seçin');
      return;
    }

    try {
      setIsImporting(true);
      setImportMessage(null);
      setError(null);
      const result = await branchesApi.importFromExcel(selectedFile);
      
      let message = `Import tamamlandı: ${result.imported} kayıt işlendi, ${result.created} yeni şube eklendi, ${result.updated} şube güncellendi.`;
      
      if (result.errors.length > 0) {
        message += `\n\nHatalar:\n${result.errors.join('\n')}`;
      }
      
      setImportMessage(message);
      await fetchBranches();
      setSelectedFile(null);
      
      // Clear message after 10 seconds
      setTimeout(() => setImportMessage(null), 10000);
    } catch (err: any) {
      setError(err.message || 'Excel import başarısız oldu');
    } finally {
      setIsImporting(false);
    }
  };

  const handleOpenImportModal = () => {
    setIsImportModalOpen(true);
    setSelectedFile(null);
    setImportMessage(null);
    setError(null);
  };

  const handleCloseImportModal = () => {
    setIsImportModalOpen(false);
    setSelectedFile(null);
    setImportMessage(null);
  };

  const handleOpenDocumentsModal = async (branch: Branch) => {
    setSelectedBranch(branch);
    setIsDocumentsModalOpen(true);
    setDocumentFiles([]);
    await fetchDocuments(branch.id);
  };

  const handleCloseDocumentsModal = () => {
    setIsDocumentsModalOpen(false);
    setSelectedBranch(null);
    setDocuments([]);
    setDocumentFiles([]);
  };

  const fetchDocuments = async (branchId: string) => {
    try {
      setIsLoadingDocuments(true);
      const data = await branchesApi.getDocuments(branchId);
      setDocuments(data);
    } catch (err: any) {
      setError(err.message || 'Evraklar yüklenirken hata oluştu');
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const handleDocumentFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setDocumentFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setDocumentFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadDocument = async () => {
    if (documentFiles.length === 0 || !selectedBranch) {
      setError('Lütfen en az bir dosya seçin');
      return;
    }

    try {
      setIsUploading(true);
      setError(null);
      
      // Upload each file
      for (const file of documentFiles) {
        await branchesApi.uploadDocument(
          selectedBranch.id,
          file,
          '', // Açıklama boş bırakılabilir
        );
      }
      
      setDocumentFiles([]);
      await fetchDocuments(selectedBranch.id);
    } catch (err: any) {
      setError(err.message || 'Dosya yükleme başarısız oldu');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!window.confirm('Bu evrakı silmek istediğinizden emin misiniz?')) {
      return;
    }

    if (!selectedBranch) return;

    try {
      await branchesApi.deleteDocument(selectedBranch.id, documentId);
      await fetchDocuments(selectedBranch.id);
    } catch (err: any) {
      setError(err.message || 'Evrak silme başarısız oldu');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getFileIcon = (fileType: string): string => {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType === 'application/pdf') return '📄';
    if (
      fileType.includes('spreadsheet') ||
      fileType.includes('excel') ||
      fileType.includes('xls')
    )
      return '📊';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    return '📎';
  };

  const columns: Column<Branch>[] = [
    { key: 'name', header: 'Şube Adı' },
    { key: 'code', header: 'Kod' },
    { key: 'city', header: 'Şehir', render: (item) => item.city || '-' },
    { key: 'phone', header: 'Telefon', render: (item) => item.phone || '-' },
    {
      key: 'documentCount',
      header: 'Evrak Sayısı',
      render: (item) => (
        <button
          onClick={() => handleOpenDocumentsModal(item)}
          className="text-blue-600 hover:text-blue-800 font-semibold"
        >
          {item.documentCount || 0} Evrak
        </button>
      ),
    },
    {
      key: 'isActive',
      header: 'Durum',
      render: (item) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {item.isActive ? 'Aktif' : 'Pasif'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'İşlemler',
      render: (item) => (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => handleOpenModal(item)}>
            Düzenle
          </Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(item.id)}>
            Sil
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text="Şubeler yükleniyor..." />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Şubeler</h1>
        <div className="flex gap-2">
          <Button 
            onClick={handleSync360} 
            variant="secondary"
            disabled={isSyncing}
          >
            {isSyncing ? 'Senkronize Ediliyor...' : '360Restoran\'dan Senkronize Et'}
          </Button>
          <Button 
            onClick={handleDownloadTemplate} 
            variant="secondary"
          >
            📥 Örnek Excel İndir
          </Button>
          <Button 
            onClick={handleOpenImportModal} 
            variant="secondary"
          >
            Excel ile Yükle
          </Button>
          <Button onClick={() => handleOpenModal()}>Yeni Şube Ekle</Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {syncMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-md">
          {syncMessage}
        </div>
      )}

      {importMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-md whitespace-pre-line">
          {importMessage}
        </div>
      )}

      <Table data={branches} columns={columns} emptyMessage="Henüz şube bulunmamaktadır" />

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingBranch ? 'Şube Düzenle' : 'Yeni Şube Ekle'}
      >
        <form onSubmit={handleSubmit}>
          <Input
            label="Şube Adı"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Şube Kodu"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            required
          />
          <Select
            label="Şirket"
            value={formData.company}
            onChange={(value) => setFormData({ ...formData, company: value })}
            options={companies.map(c => ({ value: c.id, label: c.name }))}
            placeholder="Şirket seçiniz"
          />
          <Checkbox
            label="Merkez Şube"
            checked={formData.isHeadquarters}
            onChange={(e) => setFormData({ ...formData, isHeadquarters: e.target.checked })}
          />
          <Input
            label="Adres"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          />
          <Input
            label="Şehir"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
          />
          <Input
            label="Telefon"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
          <div className="flex gap-2 mt-6">
            <Button type="submit" fullWidth>
              {editingBranch ? 'Güncelle' : 'Ekle'}
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={handleCloseModal}>
              İptal
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isImportModalOpen}
        onClose={handleCloseImportModal}
        title="Excel ile Şube Yükle"
      >
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Excel dosyanızı yüklemeden önce örnek şablonu indirip inceleyebilirsiniz.
            </p>
            <Button 
              onClick={handleDownloadTemplate} 
              variant="outline" 
              fullWidth
            >
              📥 Örnek Excel Şablonunu İndir
            </Button>
          </div>

          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Excel Dosyası Seç
            </label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100
                cursor-pointer"
            />
            {selectedFile && (
              <p className="mt-2 text-sm text-gray-600">
                Seçilen dosya: {selectedFile.name}
              </p>
            )}
          </div>

          <div className="flex gap-2 mt-6">
            <Button 
              onClick={handleImportExcel} 
              fullWidth
              disabled={!selectedFile || isImporting}
            >
              {isImporting ? 'Yükleniyor...' : 'Yükle'}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              fullWidth 
              onClick={handleCloseImportModal}
              disabled={isImporting}
            >
              İptal
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isDocumentsModalOpen}
        onClose={handleCloseDocumentsModal}
        title={`${selectedBranch?.name} - Evrak Yönetimi`}
      >
        <div className="space-y-6">
          {/* Upload Section */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Yeni Evrak Yükle</h3>
                <p className="text-sm text-gray-600">Resim, PDF, Excel veya Word dosyası yükleyebilirsiniz</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dosya Seçin (Birden fazla dosya seçebilirsiniz)
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*,.pdf,.xlsx,.xls,.doc,.docx"
                    onChange={handleDocumentFileChange}
                    multiple
                    className="block w-full text-sm text-gray-600
                      file:mr-4 file:py-3 file:px-6
                      file:rounded-lg file:border-0
                      file:text-sm file:font-semibold
                      file:bg-white file:text-blue-700
                      file:shadow-sm file:border file:border-blue-200
                      hover:file:bg-blue-50 hover:file:border-blue-300
                      file:transition-all file:duration-200
                      cursor-pointer border border-gray-200 rounded-lg
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                      bg-white"
                  />
                </div>
                {documentFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        Seçilen Dosyalar ({documentFiles.length})
                      </span>
                      <button
                        onClick={() => setDocumentFiles([])}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                        type="button"
                      >
                        Tümünü Kaldır
                      </button>
                    </div>
                    {documentFiles.map((file, index) => (
                      <div key={index} className="p-3 bg-white rounded-lg border border-blue-200 flex items-center gap-3">
                        <span className="text-3xl">{getFileIcon(file.type)}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                        </div>
                        <button
                          onClick={() => handleRemoveFile(index)}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                          type="button"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button
                onClick={handleUploadDocument}
                disabled={documentFiles.length === 0 || isUploading}
                fullWidth
              >
                {isUploading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    {documentFiles.length > 1 ? `${documentFiles.length} Dosyayı Yükle` : 'Dosyayı Yükle'}
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Documents List */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900">
                  Yüklenmiş Evraklar
                </h3>
                <span className="px-2.5 py-0.5 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full">
                  {documents.length}
                </span>
              </div>
            </div>

            {isLoadingDocuments ? (
              <div className="text-center py-12">
                <Loading text="Evraklar yükleniyor..." />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-sm font-medium text-gray-900 mb-1">Henüz evrak yüklenmemiş</p>
                <p className="text-xs text-gray-500">Yukarıdaki formu kullanarak ilk evrakınızı yükleyin</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="group bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md hover:border-blue-200 transition-all duration-200"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg flex items-center justify-center border border-blue-100">
                          <span className="text-2xl">{getFileIcon(doc.fileType)}</span>
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <a
                          href={doc.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-semibold text-gray-900 hover:text-blue-600 truncate block mb-1 transition-colors"
                        >
                          {doc.fileName}
                        </a>
                        
                        {doc.description && (
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                            {doc.description}
                          </p>
                        )}
                        
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            {formatFileSize(doc.fileSize)}
                          </span>
                          <span className="flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {new Date(doc.createdAt).toLocaleDateString('tr-TR', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </span>
                          {doc.uploadedBy && (
                            <span className="flex items-center gap-1">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              {doc.uploadedBy.name}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <a
                          href={doc.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                        </a>
                        <button
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-200">
            <Button variant="outline" onClick={handleCloseDocumentsModal}>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Kapat
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BranchList;
